import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Send, X, Phone } from "lucide-react";

const isMobile = (m) => /^(\+?61|0)4\d{8}$/.test((m || "").replace(/[\s-]/g, ""));

// Choose who gets the sign-on status: rostered Stage Commanders and/or extra phone numbers.
export function CommanderSendDialog({ eventId, day, el, allElements, savedExtras, onClose, onSent }) {
  const here = el.commanders_here || [];
  const commanders = here.length ? here : (allElements || []).flatMap((e) => e.commanders_here || []);
  const reachable = commanders.filter((c) => c.mobile);
  const [includeCmd, setIncludeCmd] = useState(reachable.length > 0);
  const [extras, setExtras] = useState(savedExtras?.length ? savedExtras : reachable.length ? [] : [""]);
  const [remember, setRemember] = useState(false);
  const [busy, setBusy] = useState(false);

  const filled = extras.map((x) => x.trim()).filter(Boolean);
  const invalid = filled.filter((x) => !isMobile(x));
  const canSend = !busy && !invalid.length && ((includeCmd && reachable.length) || filled.length);

  const setAt = (i, v) => setExtras((xs) => xs.map((x, j) => (j === i ? v : x)));

  async function send() {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/events/${eventId}/signon/commander-report`, {
        day, session: el.session, extra_mobiles: filled, include_commanders: includeCmd, remember,
      });
      toast.success(`Status sent: ${data.sms} SMS${data.email ? `, ${data.email} email` : ""}${data.extra ? ` (incl. ${data.extra} extra number${data.extra > 1 ? "s" : ""})` : ""}`);
      onSent(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto border border-black bg-white p-6" onClick={(e) => e.stopPropagation()} data-testid="commander-send-modal">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-[#002FA7]">Send sign-on status</div>
            <h3 className="mt-1 font-display text-2xl font-black tracking-tighter">{el.session}</h3>
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="commander-send-close"><X className="h-5 w-5" /></button>
        </div>

        {commanders.length ? (
          <label className="mt-4 flex cursor-pointer items-start gap-2 border border-black/15 bg-zinc-50 p-3 text-sm" data-testid="commander-send-include-label">
            <input type="checkbox" checked={includeCmd} onChange={(e) => setIncludeCmd(e.target.checked)} data-testid="commander-send-include" className="mt-0.5" />
            <span>
              <b>Rostered Stage Commanders</b> (SMS + email, with their live board link)
              <ul className="mt-1 space-y-0.5 text-xs text-zinc-600">
                {commanders.map((c, i) => <li key={i}>{c.name} · {c.role} · <span className="font-mono">{c.mobile || "no mobile"}</span></li>)}
              </ul>
            </span>
          </label>
        ) : (
          <div className="mt-4 border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="commander-send-none">
            No Stage Commander or Deputy is rostered for this element. Enter the phone number(s) to send the status to.
          </div>
        )}

        <div className="mt-4">
          <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-zinc-700"><Phone className="h-3.5 w-3.5" /> {commanders.length ? "Additional phone numbers" : "Phone numbers"}</div>
          <div className="mt-2 space-y-2">
            {extras.map((x, i) => (
              <div key={i} className="flex gap-2">
                <input value={x} onChange={(e) => setAt(i, e.target.value)} placeholder="04XX XXX XXX" inputMode="tel" data-testid={`commander-extra-mobile-${i}`}
                  className={`flex-1 border px-3 py-2 font-mono text-sm outline-none focus:border-black ${x.trim() && !isMobile(x) ? "border-red-400" : "border-black/20"}`} />
                <button onClick={() => setExtras((xs) => xs.filter((_, j) => j !== i))} aria-label="Remove number" data-testid={`commander-extra-remove-${i}`} className="border border-black/20 px-2 hover:bg-black hover:text-white"><X className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
          {extras.length < 10 ? (
            <button onClick={() => setExtras((xs) => [...xs, ""])} data-testid="commander-extra-add-btn" className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[#002FA7] hover:underline">
              <Plus className="h-3.5 w-3.5" /> Add phone number
            </button>
          ) : null}
          {invalid.length ? <p className="mt-1 text-xs text-red-700" data-testid="commander-extra-invalid">Check: {invalid.join(", ")} — use 04XX XXX XXX</p> : null}
          {filled.length ? (
            <label className="mt-3 flex items-center gap-2 text-xs text-zinc-700">
              <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} data-testid="commander-extra-remember" />
              Also send automatic status reports for this event to these numbers
            </label>
          ) : null}
        </div>

        <div className="mt-6 flex gap-2">
          <button onClick={onClose} data-testid="commander-send-cancel" className="border border-black/20 px-4 py-2 text-sm hover:border-black">Cancel</button>
          <button onClick={send} disabled={!canSend} data-testid="commander-send-confirm" className="inline-flex items-center gap-1.5 border border-[#002FA7] bg-[#002FA7] px-4 py-2 text-sm font-semibold text-white hover:bg-white hover:text-[#002FA7] disabled:opacity-40">
            <Send className="h-4 w-4" /> {busy ? "Sending…" : "Send status"}
          </button>
        </div>
      </div>
    </div>
  );
}
